import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useEffect } from "react";
import { Header } from "./components/Header";
import { HeroSection } from "./components/HeroSection";
import { ProductsSection } from "./components/ProductsSection";
import { ServicesSection } from "./components/ServicesSection";
import { ContactSection } from "./components/ContactSection";
import { Footer } from "./components/Footer";
import { LoginPage } from "./components/LoginPage";
import { AdminLayout } from "./components/admin/AdminLayout";
import { Dashboard } from "./components/admin/Dashboard";
import { MasterData } from "./components/admin/MasterData";
import { 
  Companies, 
  Branches, 
  Brands, 
  Products, 
  Categories, 
  Components, 
  Specs, 
  Racks,
  Taxes,
  Units
} from "./components/admin/MasterDataPages";
import { Inward } from "./components/admin/Inward";
import { InwardDetail } from "./components/admin/InwardDetail";
import { Inventory } from "./components/admin/Inventory";
import { InventoryDetail } from "./components/admin/InventoryDetail";
import { Vendors } from "./components/admin/Vendors";
import { PaymentTerms } from "./components/admin/PaymentTerms";
import { CompanyDetail } from "./components/admin/CompanyDetail";
import { ComingSoon } from "./components/admin/ComingSoon";
import { PurchaseBills } from "./components/admin/PurchaseBills";
import { PurchaseBillCreate } from "./components/admin/PurchaseBillCreate";
import { PaymentOut } from "./components/admin/PaymentOut";
import { PurchaseOrders } from "./components/admin/PurchaseOrders";
import { PurchaseOrderCreate } from "./components/admin/PurchaseOrderCreate";
import { DebitNotes } from "./components/admin/DebitNotes";
import { DebitNoteCreate } from "./components/admin/DebitNoteCreate";
import { Customers } from "./components/admin/Customers";
import { SaleInvoices } from "./components/admin/SaleInvoices";
import { SaleInvoiceCreate } from "./components/admin/SaleInvoiceCreate";
import { Estimates } from "./components/admin/Estimates";
import { EstimateCreate } from "./components/admin/EstimateCreate";
import { ProformaInvoices } from "./components/admin/ProformaInvoices";
import { ProformaInvoiceCreate } from "./components/admin/ProformaInvoiceCreate";
import { PaymentIn } from "./components/admin/PaymentIn";
import { PurchaseBillDetail } from "./components/admin/PurchaseBillDetail";
import { PurchaseOrderDetail } from "./components/admin/PurchaseOrderDetail";
import { DebitNoteDetail } from "./components/admin/DebitNoteDetail";
import { SaleInvoiceDetail } from "./components/admin/SaleInvoiceDetail";
import { EstimateDetail } from "./components/admin/EstimateDetail";
import { ProformaInvoiceDetail } from "./components/admin/ProformaInvoiceDetail";
import { CreditNotes } from "./components/admin/CreditNotes";
import { CreditNoteCreate } from "./components/admin/CreditNoteCreate";
import { CreditNoteDetail } from "./components/admin/CreditNoteDetail";
import { DeliveryChallans } from "./components/admin/DeliveryChallans";
import { DeliveryChallanCreate } from "./components/admin/DeliveryChallanCreate";
import { DeliveryChallanDetail } from "./components/admin/DeliveryChallanDetail";
import { Transactions } from "./components/admin/Transactions";
import { SubCategories } from "./components/admin/SubCategories";
import { PaymentModes } from "./components/admin/PaymentModes";
import { PartSaleTracker } from "./components/admin/PartSaleTracker";
import { Leads } from "./components/admin/Leads";
import { LeadDetail } from "./components/admin/LeadDetail";
import { Tickets } from "./components/admin/Tickets";
import { TicketDetail } from "./components/admin/TicketDetail";
import { ServiceItems } from "./components/admin/ServiceItems";
import { Receivables } from "./components/admin/Receivables";
import { UsersPage } from "./components/admin/UsersPage";
import { ConfigSettings } from "./components/admin/ConfigSettings";

// Remove Emergent badge
const removeEmergentBadge = () => {
  const selectors = [
    '[data-testid*="emergent"]',
    '[class*="emergent"]',
    '[id*="emergent"]',
    'div[style*="position: fixed"][style*="bottom"]',
    'a[href*="emergent"]',
  ];
  
  selectors.forEach(selector => {
    document.querySelectorAll(selector).forEach(el => {
      if (el.textContent?.includes('Emergent') || el.innerHTML?.includes('emergent')) {
        el.remove();
      }
    });
  });

  // Target fixed position elements at bottom right
  document.querySelectorAll('div, a, span').forEach(el => {
    const style = window.getComputedStyle(el);
    if (style.position === 'fixed' && style.bottom !== 'auto' && style.right !== 'auto') {
      if (el.textContent?.includes('Emergent') || el.innerHTML?.includes('Emergent')) {
        el.remove();
      }
    }
  });
};

const LandingPage = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <main>
        <HeroSection />
        <ProductsSection />
        <ServicesSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
};

function App() {
  useEffect(() => {
    // Remove badge on mount and periodically
    removeEmergentBadge();
    const interval = setInterval(removeEmergentBadge, 1000);
    
    // MutationObserver to catch dynamically added badges
    const observer = new MutationObserver(removeEmergentBadge);
    observer.observe(document.body, { childList: true, subtree: true });
    
    return () => {
      clearInterval(interval);
      observer.disconnect();
    };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/admin/login" element={<LoginPage />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Dashboard />} />
            <Route path="org-data/users" element={<UsersPage />} />
            <Route path="master-data" element={<MasterData />} />
            <Route path="config-settings" element={<ConfigSettings />} />
            <Route path="master-data/companies" element={<Companies />} />
            <Route path="master-data/companies/:id" element={<CompanyDetail />} />
            <Route path="master-data/branches" element={<Branches />} />
            <Route path="master-data/brands" element={<Brands />} />
            <Route path="master-data/products" element={<Products />} />
            <Route path="master-data/categories" element={<Categories />} />
            <Route path="master-data/components" element={<Components />} />
            <Route path="master-data/specs" element={<Specs />} />
            <Route path="master-data/racks" element={<Racks />} />
            <Route path="master-data/taxes" element={<Taxes />} />
            <Route path="master-data/units" element={<Units />} />
            <Route path="master-data/sub-categories" element={<SubCategories />} />
            <Route path="master-data/payment-modes" element={<PaymentModes />} />
            <Route path="part-sale-tracker" element={<PartSaleTracker />} />
            <Route path="services/leads" element={<Leads />} />
            <Route path="services/leads/:id" element={<LeadDetail />} />
            <Route path="services/tickets" element={<Tickets />} />
            <Route path="services/tickets/:id" element={<TicketDetail />} />
            <Route path="services/item-list" element={<ServiceItems />} />
            <Route path="services/receivables" element={<Receivables />} />
            <Route path="inward" element={<Inward />} />
            <Route path="inward/:id" element={<InwardDetail />} />
            <Route path="inventory" element={<Inventory />} />
            <Route path="inventory/:productId" element={<InventoryDetail />} />
            <Route path="master-data/vendors" element={<Vendors />} />
            <Route path="master-data/customers" element={<Customers />} />
            <Route path="master-data/payment-terms" element={<PaymentTerms />} />
            {/* Purchase & Expenses */}
            <Route path="purchase/bills" element={<PurchaseBills />} />
            <Route path="purchase/bills/new" element={<PurchaseBillCreate />} />
            <Route path="purchase/bills/:id" element={<PurchaseBillDetail />} />
            <Route path="purchase/payment-out" element={<PaymentOut />} />
            <Route path="purchase/expenses" element={<ComingSoon title="Expenses" />} />
            <Route path="purchase/orders" element={<PurchaseOrders />} />
            <Route path="purchase/orders/new" element={<PurchaseOrderCreate />} />
            <Route path="purchase/orders/:id" element={<PurchaseOrderDetail />} />
            <Route path="purchase/return" element={<DebitNotes />} />
            <Route path="purchase/return/new" element={<DebitNoteCreate />} />
            <Route path="purchase/return/:id" element={<DebitNoteDetail />} />
            {/* Sale */}
            <Route path="sale/invoices" element={<SaleInvoices />} />
            <Route path="sale/invoices/new" element={<SaleInvoiceCreate />} />
            <Route path="sale/invoices/:id" element={<SaleInvoiceDetail />} />
            <Route path="sale/estimates" element={<Estimates />} />
            <Route path="sale/estimates/new" element={<EstimateCreate />} />
            <Route path="sale/estimates/:id" element={<EstimateDetail />} />
            <Route path="sale/proforma" element={<ProformaInvoices />} />
            <Route path="sale/proforma/new" element={<ProformaInvoiceCreate />} />
            <Route path="sale/proforma/:id" element={<ProformaInvoiceDetail />} />
            <Route path="sale/payment-in" element={<PaymentIn />} />
            <Route path="sale/delivery-challan" element={<DeliveryChallans />} />
            <Route path="sale/delivery-challan/new" element={<DeliveryChallanCreate />} />
            <Route path="sale/delivery-challan/:id" element={<DeliveryChallanDetail />} />
            <Route path="sale/return" element={<CreditNotes />} />
            <Route path="sale/return/new" element={<CreditNoteCreate />} />
            <Route path="sale/return/:id" element={<CreditNoteDetail />} />
            <Route path="transactions" element={<Transactions />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
